import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';
import { InlineButtonComponent } from '../../common/buttons/inline-button/inline-button.component';
import { NModalLayoutMiddle } from '../../common/modal/layout-middle/modal-layout-middle.component';
import { NewsletterFormComponent } from '../../common/newsletter/newsletter-form.component';
import { defaultImage } from '../../icms.config';
import { NewsletterModalModalInitiatorService } from './newsletter-modal-initiator.service';

export namespace NNewsletterModal {
    export const blockId = 'newsletter-modal';

    export type Block = ICmsBlock<Catalog.IBlockData<IData>>;

    export interface IData extends NICmsContentComponent.IData {
        delay: number;
        title: string;
        image: string;
        text: string;
        bottomCopy?: string;
        buttonCopy: string;
    }
}

@DfCmsModal({
    id: NNewsletterModal.blockId,
    name: 'Newsletter Modal',
    config: (sizeId: ISizeName) => ({
        ...NModalLayoutMiddle.modalConfig,
        modalClass: 'p-a-0 p-a-4-s',
        hasBackdrop: true,
        scrollStrategy: 'block',
        forbiddenLocation: /^\/(basket|checkout)/
    }),
    allowInCms: true,
    reopenPolicy: NDfModal.ReopenPolicy.first,
    initiators: [NewsletterModalModalInitiatorService],
    blockGroup: 'modals',
    sizeRestriction: (sizeId: ISizeName) => (['S', 'M'].includes(sizeId) ? ['S', 'M'] : ['L', 'X']),
    priority: 20
})
@ICmsComponent<CmsNewsletterModalComponent>({
    id: NNewsletterModal.blockId,
    block: {
        url: NNewsletterModal.blockId
    },
    onDemandOnly: true,
    name: 'Newsletter modal',
    fields: [
        {
            key: 'delay',
            type: NICmsContentField.EFieldType.number,
            title: 'Delay',
            required: true,
            description: 'Delay time after which the popup should open. Value in seconds',
            default: 30
        },
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            required: true,
            default: defaultImage
        },
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true,
            default: 'Be in the know'
        },
        {
            key: 'text',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Text',
            required: true,
            default: 'Get 10% off your first order as well as weekly news, offers and style inspiration when you sign up for email.'
        },
        {
            key: 'bottomCopy',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Bottom copy',
            default:
                'Your privacy is important to us, please see our <a href="/customer-service/privacy">privacy policy</a>. You can unsubscribe at any time.'
        },
        {
            key: 'buttonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Button copy',
            required: true,
            default: 'No thanks, continue shopping'
        }
    ]
})
@Component({
    selector: 'cms-newsletter-modal',
    templateUrl: './cms-newsletter-modal.html',
    styles: [
        `
            :host {
                display: block;
            }
        `
    ],
    standalone: true,
    imports: [ICmsClientModule, DfModuleImageModule, InlineButtonComponent, NewsletterFormComponent, InlineButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsNewsletterModalComponent extends UiComponent implements NDfModal.IModalComponent, NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content: NNewsletterModal.IData;

    /**
     * Modal option config
     */
    modalConfig?: NDfModal.IModalConfig;

    private modalRef = inject(ModalRef);
    private dfModalService = inject(DfModalService);

    protected close(): void {
        this.modalRef.close();
        this.dfModalService.disableWebsite(this.modalConfig);
    }

    protected closeWithDelay(): void {
        this.timeoutService.setTimeout(() => {
            this.close();
        }, 3000);
    }
}
